import {useCourseCurriculum} from "@buildwitt/shapeshift-common/v1/hooks";
import {DevLog} from "@buildwitt/shapeshift-common/v1/common";
import {moduleSlim} from "@buildwitt/shapeshift-types/ICourse";
import {createContext, useReducer, ReactElement, Reducer, useEffect, Dispatch, FC, useContext, useRef} from "react";
import {useLocation, useMatch, useNavigate} from "react-router-dom";
import {INotificationSeverity, NotificationContext, AuthContext} from "@buildwitt/shapeshift-identity";
import {ICourseCurriculum} from "@buildwitt/shapeshift-types";
import {LocaleContext} from "./localeContext";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

const initialState: { curriculumState: CurriculumContextState; curriculumDispatch: Dispatch<CurriculumContextAction> } = {curriculumState: undefined, curriculumDispatch: () => { }};
const CurriculumContext = createContext(initialState);

type CurriculumContextState = ICourseCurriculum | undefined

type CurriculumContextAction = {
	type: "set";
	payload?: ICourseCurriculum;
} | {
	type: "setModuleComplete",
	module: moduleSlim,
	status: boolean
}
| {
	type: "setCourseSlug",
	slug: string,
}

interface IProps {
	children: ReactElement,
	courseSlug?: string
}

const CurriculumProvider: FC<IProps> = ({children, courseSlug}) => {
	const [{locale}] = useContext(LocaleContext)
	const {course, getCourse, setModuleComplete, apiResult} = useCourseCurriculum(courseSlug, locale)
	const {notify} = useContext(NotificationContext);
	const {jwt} = useContext(AuthContext);
	const navigate = useNavigate();
	const pageIsFeed = useMatch("/feed/*");
	const location = useLocation();
	const previousJwt = useRef<string>();

	const [curriculumState, curriculumDispatch] = useReducer<Reducer<CurriculumContextState, CurriculumContextAction>>((state: CurriculumContextState, action: CurriculumContextAction) => {
		switch (action.type) {
			case "set": {
				return action.payload;
			}
			case "setModuleComplete": {
				if (courseSlug)
					setModuleComplete(action.module, action.status);
				return state;
			}
			case "setCourseSlug": {
				getCourse(action.slug)
				return state;
			}
			default:
				return state;
		}
	}, initialState.curriculumState);

	//below is for debugging only
	useEffect(() => {
		if (curriculumState)
			DevLog("UPDATE TO CURRICULUM STATE", curriculumState);
	}, [curriculumState]);

	useEffect(() => {
		if (courseSlug)
			getCourse(courseSlug, locale);
	}, [courseSlug, locale]);

	useEffect(() => {
		if (course) {
			curriculumDispatch({type: "set", payload: course});
		}
	}, [course]);

	useEffect(() => {
		if (apiResult.isError) {
			notify("Please try again", "Something went wrong", INotificationSeverity.error, 5000);
		}
	}, [apiResult])

	useEffect(() => {
		if (jwt && previousJwt.current && jwt !== previousJwt.current && !curriculumState && courseSlug) {
			getCourse(courseSlug, locale);
		}
		previousJwt.current = jwt;
	}, [jwt, courseSlug, locale, getCourse, curriculumState]);

	return (
		<CurriculumContext.Provider value={{curriculumState, curriculumDispatch}}>
			{children}
		</CurriculumContext.Provider>
	);
};

export {
	CurriculumContext,
	CurriculumProvider
};